import React, { useState } from "react";
import { Flex } from "antd";
import { TSplitObjectResult } from "../types/components/SplitObjectDialog";
import { Col, Form, Row, Steps } from "@timeedit/ui-components/antd";
import Language from "../lib/Language";
import API from "../lib/TimeEditAPI";
import {
    TFieldDefClass,
    TObjectTypeFieldCatClass,
    TObjectCreateClass,
} from "../types/api/APIClassTypes";
import Log from "../lib/Log";
import { Button } from "@timeedit/ui-components/components/Button/Button";
import { User } from "../models/User";
import {
    useFieldValues,
    useMemberTransfer,
    usePartitionMeta,
    useSplitSize,
} from "./SplitObjectDialogHooks";
import {
    MAKE_RESERVATION,
    StepMembers,
    StepObject,
    StepReservation,
} from "./SplitObjectDialogSteps";
import { FloatingWindowFooter } from "./FloatingWindow";

type TSplitObjectDialogProps = {
    object: TObjectCreateClass;
    fieldDefs: TFieldDefClass[];
    initialSize: number;
    remainingCapacity: number;
    onDone: (res: TSplitObjectResult) => void;
    onCancel: () => void;
    templateId: number;
    reservationObjects: TObjectTypeFieldCatClass[];
    useSecondSize: boolean;
    user: User;
    isCapacitySize: boolean;
};

const LAST_STEP = 2;

// The lists adjust their height to fill their container, but every step in
// the chain down to them has to have a height for that to work.
const membersStepStyle = (active: boolean): React.CSSProperties =>
    active
        ? { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }
        : { display: "none" };

const SplitObjectDialog = (props: TSplitObjectDialogProps) => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [selectedMode, setSelectedMode] = useState<"size" | "members">("size");
    const [makeReservation, setMakeReservation] = useState<number>(MAKE_RESERVATION);

    const size = useSplitSize(props.initialSize, props.remainingCapacity);
    const fields = useFieldValues(props.object, props.fieldDefs);
    const members = useMemberTransfer(props.object);
    const meta = usePartitionMeta(props.object, props.templateId, props.useSecondSize);

    const onSubmit = () => {
        API.partitionObject(
            props.object.id,
            0,
            props.templateId,
            props.useSecondSize,
            members.selectedMembers.map((mbr) => ({ class: "objectid", id: mbr.id })),
            size.newObjectSize,
            [
                ...fields.newFieldValues.filter(
                    (field) => !meta.partitionFieldIds.includes(field.id)
                ),
                ...fields.otherFieldValues,
            ],
            (partitionObjs) => {
                const objectIds = [...partitionObjs, ...props.reservationObjects]
                    ?.map((o) => o.id)
                    .filter((id) => id !== props.object.id);

                const transformedObjects = props.reservationObjects.map((obj) =>
                    obj.id === props.object.id ? { ...obj, id: partitionObjs[0].id } : obj
                );

                props.onDone({
                    transformedObjects,
                    adjustedSize: size.newObjectSize,
                    objectIds: objectIds[0],
                    mandatoryFieldValues: fields.newFieldValues, // TODO Should this be all field values?
                    makeReservation,
                    selectedMode,
                });
            },
            (errorResult) => {
                Log.warning(errorResult.message);
            }
        );
    };

    const handleCancel = (e: React.MouseEvent) => {
        e.preventDefault();
        props.onCancel();
    };

    const canSubmit = fields.uniqueFieldsValid;

    return (
        <Form
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            requiredMark={false}
            // Provide a height for member lists to stretch to. See membersStepStyle.
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                paddingLeft: "24px",
                paddingRight: "24px",
            }}
        >
            <Row>
                <Col span={20} offset={2}>
                    <Steps
                        size="small"
                        current={currentStep}
                        style={{ marginBottom: 16 }}
                        items={[
                            { title: members.objectType ? members.objectType.name : "" },
                            { title: Language.get("dynamic_object_info_members") },
                            { title: Language.get("cal_func_res_reservation") },
                        ]}
                    />
                </Col>
            </Row>
            <div style={{ display: currentStep === 0 ? "block" : "none" }}>
                <StepObject
                    fieldDefs={props.fieldDefs}
                    partitionFieldIds={meta.partitionFieldIds}
                    fields={fields}
                    partitionMemberTypeName={members.objectType ? members.objectType.name : ""}
                />
            </div>
            <div style={membersStepStyle(currentStep === 1)}>
                <StepMembers
                    object={props.object}
                    partitionMemberTypeId={meta.partitionMemberTypeId}
                    selectedMode={selectedMode}
                    setSelectedMode={setSelectedMode}
                    initialSize={props.initialSize}
                    user={props.user}
                    size={size}
                    members={members}
                    objectName={fields?.newFieldValues[0]?.values.join(", ")}
                />
            </div>
            <div style={{ display: currentStep === LAST_STEP ? "block" : "none" }}>
                <StepReservation
                    makeReservation={makeReservation}
                    setMakeReservation={setMakeReservation}
                    isCapacitySize={props.isCapacitySize}
                    partitionMemberTypeName={members.objectType ? members.objectType.name : ""}
                />
            </div>
            <FloatingWindowFooter>
                <Flex justify="flex-end" gap="small">
                    <Button onClick={handleCancel}>{Language.get("dialog_cancel")}</Button>
                    {currentStep > 0 ? (
                        <Button onClick={() => setCurrentStep((step) => step - 1)}>
                            {Language.get("cal_res_below_previous")}
                        </Button>
                    ) : null}
                    {currentStep < LAST_STEP ? (
                        <Button
                            type="primary"
                            onClick={() => setCurrentStep((step) => step + 1)}
                            disabled={currentStep === 0 && !canSubmit}
                        >
                            {Language.get("cal_res_below_next")}
                        </Button>
                    ) : (
                        <Button type="primary" onClick={onSubmit} disabled={!canSubmit}>
                            {Language.get("nc_split_object_dialog_button_split")}
                        </Button>
                    )}
                </Flex>
            </FloatingWindowFooter>
        </Form>
    );
};

export default SplitObjectDialog;
